import React from "react";
import {
    X,
    Edit2,
    Calendar,
    Tag,
    Newspaper,
    Quote as QuoteIcon,
    ArrowUpRight
} from "lucide-react";

export default function QuickViewModal({ isOpen, onClose, item, type, onEdit }) {
    if (!isOpen || !item) return null;

    const isPress = type === "press";

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className="bg-white dark:bg-[#1A1A1A] w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-black/5 dark:border-white/5"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative h-32 bg-black overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute inset-0 flex items-center px-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/10">
                                {isPress ? <Newspaper className="text-white" size={24} /> : <QuoteIcon className="text-white" size={24} />}
                            </div>
                            <div>
                                <h2 className="text-white text-xl font-bold tracking-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                                    {isPress ? "Press Coverage" : "Featured Quote"}
                                </h2>
                                <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Quick Action View</p>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-8 p-2 text-white/40 hover:text-white bg-white/5 hover:bg-white/10 rounded-full transition-all"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8 lg:p-10">
                    <div className="space-y-8">
                        {isPress ? (
                            <>
                                <div>
                                    <div className="flex items-center gap-2 text-jazz-brown text-[10px] font-bold tracking-widest uppercase mb-2">
                                        <Tag size={12} /> {item.type}
                                    </div>
                                    <h3 className="text-2xl font-bold text-black dark:text-white leading-snug mb-3" style={{ fontFamily: "Crimson Pro, serif" }}>
                                        {item.headline}
                                    </h3>
                                    <div className="flex items-center gap-4 text-black/40 dark:text-white/40 text-xs font-medium">
                                        <div className="flex items-center gap-1.5 underline">
                                            {item.publication}
                                        </div>
                                        <span>•</span>
                                        <div className="flex items-center gap-1.5 uppercase tracking-widest">
                                            <Calendar size={14} /> {item.date_display}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 bg-[#FAFAFA] dark:bg-[#222] rounded-2xl border border-black/5 relative">
                                    <QuoteIcon size={24} className="text-jazz-brown/10 absolute top-4 right-4" />
                                    <p className="text-sm text-black/70 dark:text-white/70 italic leading-relaxed">
                                        {item.excerpt}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-4 pt-4 border-t border-black/5">
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-[10px] font-bold text-black/30 uppercase tracking-widest mb-1.5">Official Link</label>
                                        <a
                                            href={item.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs font-bold text-jazz-brown hover:text-black transition-colors flex items-center gap-1 group"
                                        >
                                            {item.link} <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                                        </a>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <label className="block text-[10px] font-bold text-black/30 uppercase tracking-widest mb-1.5">Status</label>
                                        <span className={`inline-block px-3 py-1 rounded text-[10px] font-bold tracking-widest uppercase ${item.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                            {item.status}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <p className="text-3xl font-serif italic text-black/80 dark:text-white/80 leading-relaxed mb-8" style={{ fontFamily: "Crimson Pro, serif" }}>
                                        "{item.text}"
                                    </p>
                                    <div className="flex items-center justify-between pt-6 border-t border-black/5">
                                        <div>
                                            <p className="text-xs font-bold text-black dark:text-white uppercase tracking-widest">— {item.source}</p>
                                            <p className="text-[10px] text-black/40 uppercase tracking-widest mt-0.5">Contributor / Source</p>
                                        </div>
                                        <span className={`inline-block px-3 py-1 rounded text-[10px] font-bold tracking-widest uppercase ${item.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                            {item.status}
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="mt-10 flex gap-4">
                        <button
                            onClick={onEdit}
                            className="flex-1 flex items-center justify-center gap-2 bg-black text-white py-4 rounded-2xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10"
                        >
                            <Edit2 size={16} /> EDIT ENTRY
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 py-4 rounded-2xl text-xs font-bold tracking-widest text-black/40 hover:text-black border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all uppercase"
                        >
                            Close Preview
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
